import React from "react"
import { Button, Dropdown, Typography, message } from "@mimber/ui-web"
import { MoreOutlined, SendOutlined, CloseCircleOutlined } from "@mimber/ui-web"
import {
  useResendInvitation,
  useRevokeInvitation,
} from "@mimber/client/mimber-organization"
import { usePermissionedMenuItems } from "@/core/hooks/usePermissionedMenuItems"
import { Resource, Action, InvitationStatus } from "@mimber/schema"
import type { PermissionedMenuItem } from "@/core/hooks/usePermissionedMenuItems/usePermissionedMenuItems.types"
import type { InvitationActionsDropdownProps } from "./InvitationActionsDropdown.types"

const { Text } = Typography

/**
 * Dropdown component for invitation actions (resend, revoke)
 * Handles all business logic internally including permissions and mutations
 */
export const InvitationActionsDropdown: React.FunctionComponent<
  InvitationActionsDropdownProps
> = ({
  invitation,
  onActionComplete,
  size = "small",
  type = "text",
  iconOnly = true,
}) => {
  const resendMutation = useResendInvitation()
  const revokeMutation = useRevokeInvitation()

  const handleResend = () => {
    resendMutation.mutate(
      { uuid: invitation.uuid },
      {
        onSuccess: () => {
          message.success("Invitation resent successfully")
          onActionComplete?.()
        },
        onError: (error) => {
          message.error(`Failed to resend invitation: ${error.message}`)
        },
      }
    )
  }

  const handleRevoke = () => {
    revokeMutation.mutate(
      { uuid: invitation.uuid },
      {
        onSuccess: () => {
          message.success("Invitation revoked successfully")
          onActionComplete?.()
        },
        onError: (error) => {
          message.error(`Failed to revoke invitation: ${error.message}`)
        },
      }
    )
  }

  const getActionMenuItems = (): PermissionedMenuItem[] => {
    const items: PermissionedMenuItem[] = []

    // Resend action - for PENDING and EXPIRED invitations
    if (
      invitation.status === InvitationStatus.EXPIRED ||
      invitation.status === InvitationStatus.PENDING
    ) {
      items.push({
        key: "resend",
        label: "Resend Invitation",
        icon: <SendOutlined />,
        resource: Resource.INVITATION,
        action: Action.UPDATE,
        onClick: handleResend,
      })
    }

    // Revoke action - only for PENDING invitations
    if (invitation.status === InvitationStatus.PENDING) {
      items.push({
        key: "revoke",
        label: "Revoke Invitation",
        icon: <CloseCircleOutlined />,
        danger: true,
        resource: Resource.INVITATION,
        action: Action.DELETE,
        onClick: handleRevoke,
      })
    }

    return items
  }

  const { items: menuItems } = usePermissionedMenuItems({
    items: getActionMenuItems(),
  })

  // No actions available
  if (menuItems.length === 0) {
    return <Text type="secondary">—</Text>
  }

  return (
    <Dropdown menu={{ items: menuItems }} trigger={["click"]}>
      <Button
        type={type}
        icon={iconOnly ? <MoreOutlined /> : undefined}
        size={size}
      >
        {!iconOnly && "Actions"}
      </Button>
    </Dropdown>
  )
}
